import { useState, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Champ mot de passe avec bouton afficher/masquer (œil / œil barré).
 * - Masqué par défaut.
 * - Icône à droite, à l'intérieur du champ.
 * - Accessible au clavier : bouton focusable, aria-label + aria-pressed.
 *
 * Props :
 *  - className       : appliqué au conteneur (marges, largeur…)
 *  - inputClassName  : appliqué au champ (hauteur, etc. — défaut h-11)
 *  - data-testid     : posé sur le champ ; le bouton reçoit "<testid>-toggle"
 * Toutes les autres props sont transmises à <Input> (value, onChange, placeholder, required, id…).
 */
export const PasswordInput = forwardRef(function PasswordInput(
  { className, inputClassName = "h-11", "data-testid": testId, ...props },
  ref
) {
  const [show, setShow] = useState(false);
  return (
    <div className={cn("relative", className)}>
      <Input
        ref={ref}
        type={show ? "text" : "password"}
        className={cn("pr-10", inputClassName)}
        data-testid={testId}
        {...props}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        aria-pressed={show}
        title={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        data-testid={testId ? `${testId}-toggle` : "password-toggle"}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-muted-ink transition-colors hover:text-[var(--shariti-orange)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
});
